import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Plus, Pencil, Copy, Trash2, ChevronUp, ChevronDown, Video, FileText,
  PencilRuler, HelpCircle, Eye, Upload, GripVertical, CheckCircle2, Globe, Users,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import LessonDialog, { LESSON_TYPE_META } from "@/components/courses/LessonDialog";
import LessonPreview from "@/components/courses/LessonPreview";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "Todos los niveles"];

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const coverInputRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [moduleDialog, setModuleDialog] = useState(null); // {id?, title, description}
  const [lessonDialog, setLessonDialog] = useState(null); // {moduleId, lesson?}
  const [preview, setPreview] = useState(null); // lesson object
  const [uploadingCover, setUploadingCover] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");

  const grantAccess = async () => {
    if (!grantEmail.trim()) return;
    try {
      const r = await api.post(`/courses/${courseId}/grant`, { email: grantEmail });
      toast.success(r.data.already ? "Ese estudiante ya tiene acceso" : "Acceso otorgado — ya puede ver el curso en 'Mis cursos'");
      setGrantOpen(false);
      setGrantEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/courses/${courseId}/full`);
      setCourse(r.data.course);
      setModules(r.data.modules || []);
      setSettings({
        title: r.data.course.title || "",
        subtitle: r.data.course.subtitle || "",
        description: r.data.course.description || "",
        price: r.data.course.price ?? "",
        level: r.data.course.level || "",
        progression_mode: r.data.course.progression_mode || "free",
      });
    } catch {
      toast.error("Error cargando el curso");
    } finally {
      setLoading(false);
    }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.patch(`/courses/${courseId}`, {
        title: settings.title,
        subtitle: settings.subtitle || null,
        description: settings.description || null,
        price: settings.price === "" ? null : Number(settings.price),
        level: settings.level || null,
        progression_mode: settings.progression_mode || "free",
      });
      setCourse(r.data);
      toast.success("Curso guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSavingSettings(false);
    }
  };

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/courses/${courseId}`, { status });
      setCourse(r.data);
      toast.success(status === "published" ? "¡Curso publicado! 🎉" : status === "draft" ? "Curso pasado a borrador" : "Curso archivado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/courses/${courseId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCourse((c) => ({ ...c, cover_url: r.data.cover_url, cover_file_id: r.data.cover_file_id }));
      toast.success("Portada actualizada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo portada");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  // ----- Modules -----
  const saveModule = async () => {
    const d = moduleDialog;
    if (!d.title?.trim()) { toast.error("Poné un título al módulo"); return; }
    try {
      if (d.id) {
        await api.patch(`/course-modules/${d.id}`, { title: d.title, description: d.description || null });
      } else {
        await api.post(`/courses/${courseId}/modules`, { title: d.title, description: d.description || null });
      }
      setModuleDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteModule = async (m) => {
    const ok = await confirm({
      title: "¿Eliminar módulo?",
      description: `Se borrará "${m.title}" y sus ${m.lessons.length} lecciones.`,
      confirmText: "Sí, eliminar",
    });
    if (!ok) return;
    try { await api.delete(`/course-modules/${m.id}`); load(); } catch (e) { toast.error("Error"); }
  };

  const duplicateModule = async (m) => {
    try { await api.post(`/course-modules/${m.id}/duplicate`); toast.success("Módulo duplicado"); load(); } catch { toast.error("Error"); }
  };

  const moveModule = async (idx, dir) => {
    const ids = modules.map((m) => m.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const reordered = ids.map((id) => modules.find((m) => m.id === id));
    setModules(reordered);
    try { await api.post(`/courses/${courseId}/modules/reorder`, { ids }); } catch { load(); }
  };

  // ----- Lessons -----
  const deleteLesson = async (l) => {
    const ok = await confirm({ title: "¿Eliminar lección?", description: `Se borrará "${l.title}".`, confirmText: "Sí, eliminar" });
    if (!ok) return;
    try { await api.delete(`/course-lessons/${l.id}`); load(); } catch { toast.error("Error"); }
  };

  const duplicateLesson = async (l) => {
    try { await api.post(`/course-lessons/${l.id}/duplicate`); load(); } catch { toast.error("Error"); }
  };

  const moveLesson = async (m, idx, dir) => {
    const ids = m.lessons.map((l) => l.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setModules((mods) => mods.map((mm) => mm.id === m.id ? { ...mm, lessons: ids.map((id) => mm.lessons.find((l) => l.id === id)) } : mm));
    try { await api.post(`/course-modules/${m.id}/lessons/reorder`, { ids }); } catch { load(); }
  };

  if (loading) return <div className="text-slate-500 text-sm">Cargando…</div>;
  if (!course) return <div className="text-slate-500 text-sm">Curso no encontrado.</div>;

  const statusBadge = course.status === "published"
    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Publicado</Badge>
    : course.status === "archived"
      ? <Badge className="bg-slate-100 text-slate-500 border-slate-200">Archivado</Badge>
      : <Badge className="bg-amber-100 text-amber-700 border-amber-200">Borrador</Badge>;

  return (
    <div className="space-y-6" data-testid="course-editor-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/courses")} data-testid="back-to-courses">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">{course.title} {statusBadge}</h1>
            <div className="text-xs text-slate-500">{modules.length} módulos · {modules.reduce((a, m) => a + m.lessons.length, 0)} lecciones</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setGrantOpen(true)} data-testid="grant-access-btn">
            <Users className="h-4 w-4 mr-1" /> Dar acceso
          </Button>
          {course.status === "published" && (
            <a href={`/cursos/${course.id}`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="view-sales-page-btn"><Eye className="h-4 w-4 mr-1" /> Ver página de venta</Button>
            </a>
          )}
          {course.status !== "published" && (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setStatus("published")} data-testid="publish-course-btn">
              <Globe className="h-4 w-4 mr-1" /> Publicar
            </Button>
          )}
          {course.status === "published" && (
            <Button variant="outline" onClick={() => setStatus("draft")} data-testid="unpublish-course-btn">Pasar a borrador</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Settings column */}
        <Card className="p-5 space-y-3 h-fit" data-testid="course-settings-card">
          <div className="text-sm font-semibold text-slate-900">Información del curso</div>
          <button
            type="button"
            className="relative aspect-video w-full rounded-md overflow-hidden bg-slate-100 border border-dashed border-slate-300 hover:border-brand-blue transition-colors group"
            onClick={() => coverInputRef.current?.click()}
            data-testid="cover-upload-btn"
          >
            {course.cover_url ? (
              <img src={`${backendUrl}${course.cover_url}`} alt="Portada" className="absolute inset-0 h-full w-full object-cover" />
            ) : null}
            <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 ${course.cover_url ? "bg-black/0 group-hover:bg-black/40 text-transparent group-hover:text-white" : "text-slate-400"} transition-colors`}>
              <Upload className="h-5 w-5" />
              <span className="text-xs font-medium">{uploadingCover ? "Subiendo…" : course.cover_url ? "Cambiar portada" : "Subir portada (16:9)"}</span>
            </div>
          </button>
          <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadCover} />
          <div>
            <Label className="text-xs">Título</Label>
            <Input value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} data-testid="settings-title" />
          </div>
          <div>
            <Label className="text-xs">Subtítulo</Label>
            <Textarea rows={2} value={settings.subtitle} onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })} data-testid="settings-subtitle" />
          </div>
          <div>
            <Label className="text-xs">Descripción (página de venta)</Label>
            <Textarea rows={4} value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} data-testid="settings-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Precio (USD)</Label>
              <Input type="number" min="0" step="0.01" value={settings.price} onChange={(e) => setSettings({ ...settings, price: e.target.value })} data-testid="settings-price" />
              <p className="text-[10px] text-slate-400 mt-0.5">Poné 0 para curso gratis.</p>
            </div>
            <div>
              <Label className="text-xs">Nivel</Label>
              <Select value={settings.level} onValueChange={(v) => setSettings({ ...settings, level: v })}>
                <SelectTrigger data-testid="settings-level"><SelectValue placeholder="Nivel" /></SelectTrigger>
                <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Modo de avance</Label>
            <Select value={settings.progression_mode || "free"} onValueChange={(v) => setSettings({ ...settings, progression_mode: v })}>
              <SelectTrigger data-testid="settings-progression"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Libre — puede saltar entre módulos</SelectItem>
                <SelectItem value="quiz_gate">Con candado — debe aprobar los quizzes para pasar al siguiente módulo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="btn-brand w-full" onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
            {savingSettings ? "Guardando…" : "Guardar cambios"}
          </Button>
        </Card>

        {/* Curriculum column */}
        <div className="lg:col-span-2 space-y-4" data-testid="curriculum-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Contenido del curso</div>
            <Button size="sm" variant="outline" onClick={() => setModuleDialog({ title: "", description: "" })} data-testid="add-module-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar módulo
            </Button>
          </div>

          {modules.length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-500" data-testid="curriculum-empty">
              Empezá agregando el primer módulo (ej: "Módulo 1 — Saludos y presentaciones").
            </Card>
          )}

          {modules.map((m, mi) => (
            <Card key={m.id} className="overflow-hidden" data-testid={`module-card-${m.id}`}>
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                <GripVertical className="h-4 w-4 text-slate-300" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">Módulo {mi + 1} — {m.title}</div>
                  {m.description && <div className="text-xs text-slate-500 truncate">{m.description}</div>}
                </div>
                <div className="flex items-center gap-0.5">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={mi === 0} onClick={() => moveModule(mi, -1)} data-testid={`module-up-${m.id}`}><ChevronUp className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={mi === modules.length - 1} onClick={() => moveModule(mi, 1)} data-testid={`module-down-${m.id}`}><ChevronDown className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Editar" onClick={() => setModuleDialog({ id: m.id, title: m.title, description: m.description || "" })} data-testid={`module-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Duplicar" onClick={() => duplicateModule(m)} data-testid={`module-dup-${m.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" title="Eliminar" onClick={() => deleteModule(m)} data-testid={`module-del-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {m.lessons.map((l, li) => {
                  const meta = LESSON_TYPE_META[l.type] || {};
                  const Icon = { video: Video, document: FileText, practice: PencilRuler, quiz: HelpCircle }[l.type] || FileText;
                  return (
                    <div key={l.id} className="flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50" data-testid={`lesson-row-${l.id}`}>
                      <span className={`p-1.5 rounded-md ${meta.iconBg || "bg-slate-100 text-slate-500"}`}><Icon className="h-3.5 w-3.5" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{l.title}</div>
                        <div className="text-[11px] text-slate-400 flex items-center gap-2">
                          {meta.label}
                          {l.duration_minutes ? <span>· {l.duration_minutes} min</span> : null}
                          {l.type === "quiz" && <span>· {(l.quiz_questions || []).length} preguntas · aprueba con {l.quiz_pass_pct ?? 70}%</span>}
                          {l.is_free_preview && <Badge variant="outline" className="text-[9px] h-4 px-1 text-emerald-700 border-emerald-300">Muestra gratis</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={li === 0} onClick={() => moveLesson(m, li, -1)} data-testid={`lesson-up-${l.id}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={li === m.lessons.length - 1} onClick={() => moveLesson(m, li, 1)} data-testid={`lesson-down-${l.id}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Vista previa" onClick={() => setPreview(l)} data-testid={`lesson-preview-${l.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Editar" onClick={() => setLessonDialog({ moduleId: m.id, lesson: l })} data-testid={`lesson-edit-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Duplicar" onClick={() => duplicateLesson(l)} data-testid={`lesson-dup-${l.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" title="Eliminar" onClick={() => deleteLesson(l)} data-testid={`lesson-del-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  );
                })}
                <div className="px-4 py-2.5">
                  <Button size="sm" variant="ghost" className="text-brand-blue" onClick={() => setLessonDialog({ moduleId: m.id })} data-testid={`add-lesson-${m.id}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Agregar lección
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Module dialog */}
      <Dialog open={!!moduleDialog} onOpenChange={(o) => !o && setModuleDialog(null)}>
        <DialogContent className="max-w-md" data-testid="module-dialog">
          <DialogHeader><DialogTitle>{moduleDialog?.id ? "Editar módulo" : "Nuevo módulo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título *</Label>
              <Input value={moduleDialog?.title || ""} onChange={(e) => setModuleDialog({ ...moduleDialog, title: e.target.value })} placeholder="Ej: Saludos y presentaciones" data-testid="module-title-input" />
            </div>
            <div>
              <Label className="text-xs">Descripción</Label>
              <Textarea rows={2} value={moduleDialog?.description || ""} onChange={(e) => setModuleDialog({ ...moduleDialog, description: e.target.value })} data-testid="module-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(null)}>Cancelar</Button>
            <Button className="btn-brand" onClick={saveModule} data-testid="module-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson dialog */}
      {lessonDialog && (
        <LessonDialog
          open={!!lessonDialog}
          moduleId={lessonDialog.moduleId}
          lesson={lessonDialog.lesson}
          onClose={() => setLessonDialog(null)}
          onSaved={() => { setLessonDialog(null); load(); }}
        />
      )}

      {/* Grant access dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="max-w-sm" data-testid="grant-dialog">
          <DialogHeader><DialogTitle>Dar acceso al curso</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Activación manual: el usuario debe existir (estudiante con cuenta). Verá el curso en "Mis cursos".</p>
          <Input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="email@estudiante.com" data-testid="grant-email-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancelar</Button>
            <Button className="btn-brand" onClick={grantAccess} data-testid="grant-submit-btn">Dar acceso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson preview */}
      {preview && <LessonPreview lesson={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
